import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Confirm, Icon, List, Rating } from 'semantic-ui-react'
import './SkillList.css'

/**
 * Renders a list of skills/languages with inline star rating and delete action.
 * Each item shows the name, a visual Rating (interactive if editable), and a delete button.
 *
 * Props:
 *   items          {Array}     Array of objects: { id, name, level }
 *   editable       {boolean}   Whether the user can edit/delete items (true on /cvGenerator)
 *   onLevelChange  {Function}  Callback when rating changes. Signature: fn(id, newLevel)
 *   onDelete       {Function}  Callback when item is deleted. Signature: fn(id)
 *   maxRating      {number}    [optional] Max stars to display. Default: 5
 *   emptyMessage   {string}    [optional] Message shown when items is empty. Default: "No items added yet."
 */
const SkillList = ({ items, editable, onLevelChange, onDelete, maxRating, emptyMessage }) => {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)

  const max = maxRating || 5

  const handleRate = (id, e, data) => {
    if (!editable || !onLevelChange) return
    onLevelChange(id, data.rating)
  }

  const handleDeleteClick = (id) => {
    setPendingDeleteId(id)
    setConfirmOpen(true)
  }

  const handleConfirm = () => {
    if (onDelete && pendingDeleteId !== null) {
      onDelete(pendingDeleteId)
    }
    setConfirmOpen(false)
    setPendingDeleteId(null)
  }

  const handleCancel = () => {
    setConfirmOpen(false)
    setPendingDeleteId(null)
  }

  if (!items || items.length === 0) {
    return (
      <div className="skill-list-empty">
        <Icon name="info circle" />
        {emptyMessage || 'No items added yet.'}
      </div>
    )
  }

  return (
    <div className="skill-list-wrapper">
      <List divided relaxed className="skill-list">
        {items.map(function (item) {
          return (
            <List.Item key={item.id} className="skill-list-item">
              <List.Content className="skill-list-content">
                <span className="skill-list-name">{item.name}</span>
                <span className="skill-list-rating">
                  <Rating
                    icon="star"
                    rating={item.level}
                    maxRating={max}
                    size="large"
                    disabled={!editable}
                    onRate={function (e, data) { handleRate(item.id, e, data) }}
                  />
                </span>
                {editable && (
                  <span className="skill-list-actions">
                    <Button
                      icon
                      size="mini"
                      color="red"
                      basic
                      onClick={function () { handleDeleteClick(item.id) }}
                      title="Delete"
                    >
                      <Icon name="trash" />
                    </Button>
                  </span>
                )}
              </List.Content>
            </List.Item>
          )
        })}
      </List>

      <Confirm
        open={confirmOpen}
        header="Confirm deletion"
        content="Are you sure you want to remove this item?"
        confirmButton="Delete"
        cancelButton="Cancel"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        size="mini"
      />
    </div>
  )
}

SkillList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      level: PropTypes.number.isRequired,
    })
  ).isRequired,
  editable: PropTypes.bool.isRequired,
  onLevelChange: PropTypes.func,
  onDelete: PropTypes.func,
  maxRating: PropTypes.number,
  emptyMessage: PropTypes.string,
}

export default SkillList
